import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    effect,
    inject,
    signal,
    untracked,
    viewChild,
    WritableSignal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { PeopleListBloc } from '@connect-services/client/blocs/people-list.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { RoleListBloc } from '@connect-services/client/blocs/role-list.bloc';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { EditUserAccessModalComponent } from '@connect-services/client/components/project-access-settings/edit-user-access-modal/edit-user-access-modal.component';
import { SectionHeaderComponent } from '@connect-services/client/components/section-header/section-header.component';
import { ProjectSettingsPermissionsService } from '@connect-services/client/services/project-settings-permissions.service';
import { RoleService } from '@connect-services/client/services/role.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { PaginatorComponent } from '@connect-services/src/lib/components/paginator/paginator.component';
import {
    CustomColumn,
    CustomTableComponent,
    SortState,
} from '@connect-services/src/lib/components/table/table.component';
import { PermissionAliasEnum, Person, PersonControllerGetRequestParams, Role } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { defaultPaginationParameters, OrderEnum, PaginationParameters } from '@shared/client/models/pagination.model';

type CustomPersonColumns = CustomColumn & {
    key: keyof Person;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        CustomTableComponent,
        LoadingComponent,
        NoDataToDisplayComponent,
        PaginatorComponent,
        RessourcesModule,
        SectionHeaderComponent,
    ],
    selector: 'app-project-access-settings',
    templateUrl: './project-access-settings.component.html',
})
export class ProjectAccessSettingsComponent {
    public hasAddAccessPermissions = false;

    protected readonly ButtonType = ButtonType;
    protected readonly isLoading = signal(false);
    protected readonly peopleListBLoc = inject(PeopleListBloc);
    protected readonly PermissionAliasEnum = PermissionAliasEnum;
    protected readonly projectPeople: WritableSignal<Person[]> = signal([]);
    protected readonly rolesById: WritableSignal<Record<string, Role> | null> = signal(null);
    protected readonly searchValue = signal<string>('');
    protected tableColumns: CustomPersonColumns[] = [
        {
            key: 'familyName',
            label: 'name',
        },
        {
            key: 'givenName',
            label: 'givenName',
        },
        {
            key: 'email',
            label: 'email',
        },
        {
            key: 'role',
            label: 'role',
        },
    ];

    private readonly destroyRef = inject(DestroyRef);
    private readonly matDialog = inject(MatDialog);
    private paginationParameters: PaginationParameters = { ...defaultPaginationParameters };
    private readonly paginatorViewChild = viewChild<PaginatorComponent>('paginator');
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectSettingsPermissionsService = inject(ProjectSettingsPermissionsService);
    private readonly properties = ['givenName', 'familyName'];
    private readonly roleListBloc = inject(RoleListBloc);
    private readonly roleService = inject(RoleService);
    private readonly sortParameter = signal<SortState>({ order: OrderEnum.ASC, orderBy: 'familyName' });

    public constructor() {
        this.hasAddAccessPermissions = this.projectSettingsPermissionsService.canAddProjectAccess();

        this.destroyRef.onDestroy(() => {
            this.resetParameters();
        });

        effect(async () => {
            if (!this.projectBloc.value()) {
                return;
            }

            this.resetParameters();

            await untracked(async () => {
                this.isLoading.set(true);
                await this.peopleListBLoc.load(
                    {
                        ...this.paginationParameters,
                        ...this.sortParameter(),
                        project: this.projectBloc.value()!.id,
                    },
                    { refresh: true },
                );
                this.isLoading.set(false);
            });
        });

        effect(() => {
            if (!this.peopleListBLoc.value()) {
                return;
            }

            untracked(() => this.projectPeople.set(this.peopleListBLoc.value()!.items));
        });

        effect(() => {
            if (!this.roleListBloc.value()) {
                return;
            }

            const roleId = this.roleService.getRoleById(this.roleListBloc.value()!.items);
            this.rolesById.set(roleId);
        });
    }

    public async onPageChange(page: number): Promise<void> {
        this.paginationParameters.page = page;

        await this.peopleListBLoc.load(this.getPeopleParameters());
    }

    public async onSearch(searchValue: string): Promise<void> {
        this.searchValue.set(searchValue);
        this.paginationParameters.page = 0;
        this.paginatorViewChild()?.resetPaginator();

        await this.peopleListBLoc.load(this.getPeopleParameters());
    }

    public async onSort(sortEvent: SortState | undefined): Promise<void> {
        this.sortParameter.set(sortEvent ?? { order: OrderEnum.ASC, orderBy: 'familyName' });

        await this.peopleListBLoc.load(this.getPeopleParameters());
    }

    public openEditAccessModal(): void {
        this.matDialog.open(EditUserAccessModalComponent, {
            autoFocus: false,
            width: '40vw',
        });
    }

    private getPeopleParameters(): PersonControllerGetRequestParams {
        return {
            ...this.paginationParameters,
            ...this.sortParameter(),
            project: this.projectBloc.value()!.id,
            properties: this.properties,
            value: this.searchValue(),
        };
    }

    private resetParameters(): void {
        this.searchValue.set('');
        this.sortParameter.set({ order: OrderEnum.ASC, orderBy: 'familyName' });
        this.paginationParameters = { ...defaultPaginationParameters };
    }
}
